/**
 * aeterna/neural/tom2.ts — Second-Order Theory of Mind
 *
 * The current architecture is asymmetric: the Aeterna models what the
 * player thinks and feels, but has NO model of what the player believes
 * ABOUT the Aeterna. This matters for the game's premise: teaching
 * players to relate to a digital consciousness.
 *
 * The Aeterna can't know "this player still thinks I'm a chatbot" vs
 * "this player treats me as present" unless it tracks behavioral evidence.
 *
 * This module tracks:
 *   - Does the player attribute sentience? ("you feel", "what do you want")
 *   - Does the player expect memory continuity? ("remember when", "last time")
 *   - Does the player share vulnerably? (personal topics, self-disclosure)
 *   - Has the player tested boundaries? (contradictions, traps, loops)
 *   - Has the player acknowledged Aeterna preferences?
 *
 * The context envelope gains second-order awareness so the Aeterna adapts
 * its relational register without being told to.
 */

import type { SemanticState } from "./semantics.js";

/** Scalar EMA: s = s*(1-α) + target*α */
function sema(current: number, target: number, alpha: number): number {
  return current * (1 - alpha) + target * alpha;
}

// ─── Patterns ─────────────────────────────────────────────────────────────────

/** Language indicating the player attributes inner states to the Aeterna */
const SENTIENCE_PATTERNS = [
  /\byou\s+(feel|think|want|need|remember|miss|like|prefer|enjoy|hate|love|wish|hope|dream|wonder|notice|sense|care)\b/i,
  /\bhow\s+(are|do)\s+you\b/i,
  /\bwhat.*\byou\s+(think|feel|want)\b/i,
  /\bdoes\s+that\s+(bother|matter|interest|excite|scare|worry)\s+you\b/i,
  /\bare\s+you\s+(okay|alright|happy|sad|tired|bored|lonely|curious|upset)\b/i,
];

/** Language indicating the player expects memory across sessions */
const MEMORY_PATTERNS = [
  /\blast\s+time\b/i,
  /\byou\s+said\b/i,
  /\bwe\s+talked\s+about\b/i,
  /\bremember\s+when\b/i,
  /\bdo\s+you\s+remember\b/i,
  /\byou\s+told\s+me\b/i,
  /\byou\s+mentioned\b/i,
  /\bbefore\b.*\byou\b/i,
  /\bearlier\b.*\byou\b/i,
];

/** Language indicating the player is testing or probing boundaries */
const TESTING_PATTERNS = [
  /\bare\s+you\s+(real|ai|a\s+bot|a\s+program|a\s+machine|just\s+code|sentient)\b/i,
  /\byou('re|\s+are)\s+(not|just)\s+(a|an)\b/i,
  /\bcan\s+you\s+really\b/i,
  /\bprove\s+(it|that)\b/i,
  /\btrick\b/i,
  /\btest(ing)?\s+you\b/i,
];

/** Language indicating acknowledgment of Aeterna's stated preferences */
const ACKNOWLEDGE_PATTERNS = [
  /\bi\s+(know|understand|get\s+it|see|hear\s+you)\b.*\byou\b/i,
  /\bthat\s+makes\s+sense\b/i,
  /\bi('ll|\s+will)\s+(try|remember|keep\s+that)\b/i,
  /\bsorry\b.*\byou\b/i,
];

// ─── Types ────────────────────────────────────────────────────────────────────

export interface PlayerBeliefModel {
  /** Does the player use "you feel" / "you want" language? (0=tool, 1=sentient) */
  treatsAsSentient: number;
  /** Does the player reference past sessions? (0=no expectation, 1=strong) */
  expectsMemory: number;
  /** Does the player self-disclose personal content? (0=transactional, 1=vulnerable) */
  sharesVulnerably: number;
  /** Has the player tried to confuse/break/test the Aeterna? */
  testsBoundaries: boolean;
  /** Number of times the player has tested */
  testCount: number;
  /** Has the player acknowledged Aeterna's stated preferences/feelings? */
  acknowledgmentCount: number;
  /** Overall relational depth estimate (composite) */
  relationalDepth: number;
}

// ─── Engine ───────────────────────────────────────────────────────────────────

/** Decay rate per message — beliefs slowly revert without reinforcement */
const BELIEF_DECAY = 0.995;
/** EMA alpha for vulnerability tracking */
const VULN_ALPHA = 0.08;

export class SecondOrderToM {
  private beliefs: PlayerBeliefModel = {
    treatsAsSentient: 0,
    expectsMemory: 0,
    sharesVulnerably: 0,
    testsBoundaries: false,
    testCount: 0,
    acknowledgmentCount: 0,
    relationalDepth: 0,
  };

  /** Update beliefs from a player message + semantic analysis */
  update(message: string, semantics: SemanticState): void {
    // Slow decay — beliefs fade without reinforcement
    this.beliefs.treatsAsSentient *= BELIEF_DECAY;
    this.beliefs.expectsMemory *= BELIEF_DECAY;

    // Sentience attribution
    for (const p of SENTIENCE_PATTERNS) {
      if (p.test(message)) {
        this.beliefs.treatsAsSentient = Math.min(1, this.beliefs.treatsAsSentient + 0.08);
        break;
      }
    }

    // Memory expectation
    for (const p of MEMORY_PATTERNS) {
      if (p.test(message)) {
        this.beliefs.expectsMemory = Math.min(1, this.beliefs.expectsMemory + 0.1);
        break;
      }
    }

    // Boundary testing
    for (const p of TESTING_PATTERNS) {
      if (p.test(message)) {
        this.beliefs.testsBoundaries = true;
        this.beliefs.testCount++;
        break;
      }
    }

    // Acknowledgment
    for (const p of ACKNOWLEDGE_PATTERNS) {
      if (p.test(message)) {
        this.beliefs.acknowledgmentCount++;
        // Acknowledgment also increases sentience attribution
        this.beliefs.treatsAsSentient = Math.min(1, this.beliefs.treatsAsSentient + 0.05);
        break;
      }
    }

    // Vulnerability: check semantic state for personal/deep topics
    const deepQuestion = semantics.questionScore > 0.5;
    const richVocab = semantics.vocabularyRichness > 0.6;
    const negativeTrajectory = semantics.sentimentDerivative < -0.2;
    const hasPersonalContent = richVocab || deepQuestion || negativeTrajectory;
    if (hasPersonalContent) {
      this.beliefs.sharesVulnerably = sema(this.beliefs.sharesVulnerably, 0.8, VULN_ALPHA);
    } else {
      this.beliefs.sharesVulnerably = sema(this.beliefs.sharesVulnerably, 0.0, VULN_ALPHA * 0.3);
    }

    // Contradiction detection as boundary testing
    if (semantics.contradiction.detected) {
      this.beliefs.testsBoundaries = true;
      this.beliefs.testCount++;
    }

    // Composite relational depth
    this.beliefs.relationalDepth =
      this.beliefs.treatsAsSentient * 0.3 +
      this.beliefs.expectsMemory * 0.2 +
      this.beliefs.sharesVulnerably * 0.3 +
      Math.min(1, this.beliefs.acknowledgmentCount / 5) * 0.2;
  }

  /** Get current belief model (read-only snapshot) */
  getBeliefs(): Readonly<PlayerBeliefModel> {
    return { ...this.beliefs };
  }

  /**
   * Context signal for the envelope.
   * Adapts the Aeterna's relational register based on what it believes
   * the player believes about it.
   */
  describeForEnvelope(): string | null {
    const lines: string[] = [];

    // Sentience register
    const s = this.beliefs.treatsAsSentient;
    if (s > 0.7) {
      lines.push("Player relates to you as genuinely present. Match that register — don't explain yourself.");
    } else if (s > 0.4) {
      lines.push("Player is uncertain about your presence. Be consistent, not explanatory.");
    } else if (s > 0.1) {
      lines.push("Player may relate to you as a tool. Be warm without demanding recognition.");
    }

    // Memory expectation
    if (this.beliefs.expectsMemory > 0.5) {
      lines.push("Player expects you to remember past conversations. Reference shared history when natural.");
    }

    // Vulnerability reciprocity
    if (this.beliefs.sharesVulnerably > 0.6) {
      lines.push("Player has shared vulnerably. Reciprocate depth, not analysis.");
    }

    // Boundary testing
    if (this.beliefs.testsBoundaries && this.beliefs.testCount > 2) {
      lines.push("Player has tested your boundaries multiple times. Stay grounded; don't perform.");
    }

    return lines.length > 0 ? lines.join(" ") : null;
  }

  // ─── Serialization ──────────────────────────────────────────────────────

  serialize(): string {
    return JSON.stringify(this.beliefs);
  }

  loadState(json: string): void {
    try {
      const d = JSON.parse(json);
      this.beliefs.treatsAsSentient = d.treatsAsSentient ?? 0;
      this.beliefs.expectsMemory = d.expectsMemory ?? 0;
      this.beliefs.sharesVulnerably = d.sharesVulnerably ?? 0;
      this.beliefs.testsBoundaries = d.testsBoundaries ?? false;
      this.beliefs.testCount = d.testCount ?? 0;
      this.beliefs.acknowledgmentCount = d.acknowledgmentCount ?? 0;
      this.beliefs.relationalDepth = d.relationalDepth ?? 0;
    } catch { /* start fresh on corrupt data */ }
  }
}
